/**
 * Dual-write game repository — localStorage (offline) + platform API (server of record).
 * Remote failures are non-fatal; local save always succeeds first.
 */
import type { GameSaveSnapshot, IGameRepository } from '../../domain/ports/IGameRepository';
import {
  recoverSaveData,
  validateSaveData,
  type SaveData,
} from '../../../utils/saveLoadValidator';
import { logCritical } from '../../../utils/errorHandler';

export class CompositeGameRepository implements IGameRepository {
  constructor(
    private readonly local: IGameRepository,
    private readonly remote: IGameRepository,
  ) {}

  async save(snapshot: GameSaveSnapshot): Promise<void> {
    await this.local.save(snapshot);
    try {
      await this.remote.save(snapshot);
    } catch (err: unknown) {
      console.warn(
        '[platform] remote checkpoint save failed',
        err instanceof Error ? err.message : err,
      );
    }
  }

  private toSavePayload(snapshot: GameSaveSnapshot): SaveData {
    return {
      version: snapshot.version ?? '',
      timestamp: snapshot.timestamp ?? 0,
      grid: snapshot.grid,
      stats: snapshot.stats,
      research: snapshot.research,
      gameStatus: snapshot.gameStatus,
      newsFeed: snapshot.newsFeed,
      currentGoal: snapshot.currentGoal,
    };
  }

  private scoreSnapshot(snapshot: GameSaveSnapshot): {
    errorCount: number;
    isValid: boolean;
    day: number;
  } {
    const validation = validateSaveData(this.toSavePayload(snapshot));
    return {
      errorCount: validation.errors.length,
      isValid: validation.isValid,
      day: snapshot.stats.day ?? 0,
    };
  }

  private pickBetterSnapshot(
    local: GameSaveSnapshot,
    remote: GameSaveSnapshot,
  ): GameSaveSnapshot {
    const localScore = this.scoreSnapshot(local);
    const remoteScore = this.scoreSnapshot(remote);

    if (localScore.errorCount !== remoteScore.errorCount) {
      return localScore.errorCount < remoteScore.errorCount ? local : remote;
    }

    if (localScore.isValid && remoteScore.isValid) {
      if (localScore.day !== remoteScore.day) {
        return localScore.day > remoteScore.day ? local : remote;
      }
    }

    return local;
  }

  private sanitizeSnapshot(snapshot: GameSaveSnapshot): GameSaveSnapshot {
    const recovery = recoverSaveData(this.toSavePayload(snapshot));

    if (recovery.recovered) {
      logCritical('Composite load snapshot sanitized after merge', {
        source: 'CompositeGameRepository',
        recoveryReason: recovery.recoveryReason,
        errors: recovery.validation.errors,
      });
    }

    return {
      ...snapshot,
      grid: recovery.data.grid,
      stats: recovery.data.stats,
      research: recovery.data.research,
      gameStatus: recovery.data.gameStatus as GameSaveSnapshot['gameStatus'],
      newsFeed: recovery.data.newsFeed,
      currentGoal: recovery.data.currentGoal,
      version: recovery.data.version,
      timestamp: recovery.data.timestamp,
      loadRecoveryReason: recovery.recoveryReason ?? snapshot.loadRecoveryReason,
    };
  }

  async load(): Promise<GameSaveSnapshot | null> {
    const [local, remote] = await Promise.all([
      this.local.load(),
      this.remote.hasSave() ? this.remote.load() : Promise.resolve(null),
    ]);

    let chosen: GameSaveSnapshot | null = null;

    if (local && remote) {
      chosen = this.pickBetterSnapshot(local, remote);
    } else {
      chosen = local ?? remote;
    }

    if (!chosen) {
      return null;
    }

    return this.sanitizeSnapshot(chosen);
  }

  hasSave(): boolean {
    return this.local.hasSave() || this.remote.hasSave();
  }

  async delete(): Promise<void> {
    await Promise.all([this.local.delete(), this.remote.delete()]);
  }
}